/**
 * v3.21-1715 — Deal Operations Workspace
 *
 * Rebuilt PTC Deal Pipeline as a LIST/QUEUE with a right-side Deal
 * drawer.  Replaces the old giant stage-card wall + inline expanded
 * client card layout.  One row = one real Deal.  Multiple deals for
 * the same client render as separate rows.
 *
 * Sources of truth:
 *   • `/api/ptc-deals`       — real ptc_deals rows (Deal SSOT)
 *   • `/api/clients`         — client info + legacy client-level deals
 *   • `/api/clients/{id}/ownerships`
 *
 * Deep-link support:
 *   `?deal=<deal_id>` auto-opens the drawer for that deal (used by
 *   Ghost after Confirm & Create Deal).
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Search, RefreshCw, Loader2, FileText, Mail, ClipboardList, Zap,
  Phone, ArrowRight, X, Building2, DollarSign, Home,
  ExternalLink, FileCheck, Activity as ActivityIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import useDocumentTitle from '../hooks/useDocumentTitle';
// v3.21-1741: DealCompensationVisibility import removed — the block was
// removed from the deal page layout because it never functioned in
// practice and consumed dead vertical space.

// ── Stage catalog (canonical order) ──────────────────────────────────
const STAGES = [
  { id: 'new_lead',            label: 'New Lead',            color: 'bg-slate-500' },
  { id: 'contacted',           label: 'Contacted',           color: 'bg-blue-500' },
  { id: 'credibility_sent',    label: 'Credibility Sent',    color: 'bg-indigo-500' },
  { id: 'info_request_sent',   label: 'Info Request Sent',   color: 'bg-purple-500' },
  { id: 'info_received',       label: 'Info Received',       color: 'bg-amber-500' },
  { id: 'proposal_sent',       label: 'Proposal Sent',       color: 'bg-orange-500' },
  { id: 'contract_sent',       label: 'Contract Sent',       color: 'bg-cyan-500' },
  { id: 'contract_signed',     label: 'Contract Signed',     color: 'bg-sky-600' },
  { id: 'payment_setup',       label: 'Payment Setup',       color: 'bg-rose-500' },
  { id: 'payment_in_progress', label: 'Payment In Progress', color: 'bg-pink-500' },
  { id: 'threshold_met',       label: 'Threshold Met',       color: 'bg-lime-600' },
  { id: 'submitted_to_partner',label: 'Submitted',           color: 'bg-teal-600' },
  { id: 'closed',              label: 'Closed',              color: 'bg-emerald-500' },
];
const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.id, s]));

const NEXT_ACTION = {
  new_lead:            { label: 'Make Contact',                     icon: Phone },
  contacted:           { label: 'Send Credibility Email',           icon: Mail },
  credibility_sent:    { label: 'Send Info Request',                icon: ClipboardList },
  info_request_sent:   { label: 'Wait for Info',                    icon: ClipboardList },
  info_received:       { label: 'Create Service Authorization',     icon: FileText },
  proposal_sent:       { label: 'Send Contract',                    icon: FileText },
  contract_sent:       { label: 'Send Contract Link',               icon: FileText },
  contract_signed:     { label: 'Setup Payment',                    icon: DollarSign },
  payment_setup:       { label: 'Collect Payment',                  icon: DollarSign },
  payment_in_progress: { label: 'Collect Payment',                  icon: DollarSign },
  threshold_met:       { label: 'Submit to Partner',                icon: ArrowRight },
  submitted_to_partner:{ label: 'Awaiting Partner',                 icon: ClipboardList },
  closed:              { label: 'Complete',                         icon: FileCheck },
};

const money = v => v != null && v !== '' ? `$${Number(v).toLocaleString(undefined, {maximumFractionDigits: 0})}` : '—';
const shortId = v => v ? String(v).slice(0, 8) : '—';
const timeAgo = d => {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

function deriveStageFromClient(c) {
  const ds = (c.deal_stage || '').toLowerCase();
  const p = c.ptc_progress || {};
  if (STAGE_MAP[ds]) return ds;
  if (['funded','completed','closed','settlement','vendor_payment_received','customer_funded'].includes(ds)) return 'closed';
  if (p.contract_signed) return 'contract_signed';
  if (p.contract_sent)   return 'contract_sent';
  if (p.proposal_sent)   return 'proposal_sent';
  if (p.timeshare_info_received || p.proposal_created) return 'info_received';
  if (p.timeshare_info_requested) return 'info_request_sent';
  if (p.credibility_email_sent) return 'credibility_sent';
  return 'new_lead';
}

// ── Page ─────────────────────────────────────────────────────────────
export default function DealOperationsWorkspacePage() {
  useDocumentTitle('Deal Operations Workspace');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rawDeals,   setRawDeals]   = useState([]);
  const [rawClients, setRawClients] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query,      setQuery]      = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [openDealKey, setOpenDealKey] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [d, cResp] = await Promise.all([
        api.get('/ptc-deals', { params: { limit: 500 } }).catch(() => ({ data: [] })),
        api.get('/clients',   { params: { limit: 500, include_metrics: false } }),
      ]);
      setRawDeals(Array.isArray(d.data) ? d.data : (d.data.deals || []));
      setRawClients(cResp.data.clients || cResp.data || []);
    } catch (e) { console.error(e); toast.error('Failed to load workspace'); }
  }, []);

  useEffect(() => { setLoading(true); fetchAll().finally(() => setLoading(false)); }, [fetchAll]);
  const refresh = () => { setRefreshing(true); fetchAll().finally(() => setRefreshing(false)); };

  // ── Merge real Deals + legacy client-level deals ───────────────────
  // One row per ptc_deals row.  Clients with deal_amount>0 but NO
  // ptc_deals rows get a single legacy row keyed by client id.
  const rows = useMemo(() => {
    const clientById = Object.fromEntries(rawClients.map(c => [c.id, c]));
    const dealClientIds = new Set(rawDeals.map(d => d.client_id).filter(Boolean));
    const out = [];
    // Real deals
    for (const d of rawDeals) {
      // v3.21-1715 · hide rows that repoint to a merged duplicate.
      if (d.client_id && clientById[d.client_id]?.status === 'MERGED_TEST_DUPLICATE') continue;
      const c = clientById[d.client_id] || {};
      out.push({
        key:            `deal:${d.id}`,
        is_legacy:      false,
        deal_id:        d.id,
        deal_number:    d.deal_number || shortId(d.id).toUpperCase(),
        client_id:      d.client_id,
        client_name:    c.full_name || d.client_name || '(unknown)',
        client_email:   c.email     || d.client_email || '',
        client_phone:   c.phone     || '',
        deal_stage:     (d.deal_stage || 'new_lead').toLowerCase(),
        deal_amount:    Number(d.price || d.total_deal_price || 0),
        collected:      Number(c.amount_collected || 0),
        source:         d.deal_source || c.deal_source || '—',
        rep:            c.sales_rep_name || d.advisor_name || '—',
        last_activity:  d.updated_at || d.created_at,
        raw_deal:       d,
        raw_client:     c,
      });
    }
    // Legacy client-level deals (no ptc_deals row yet)
    for (const c of rawClients) {
      if (dealClientIds.has(c.id)) continue;
      if (c.status === 'MERGED_TEST_DUPLICATE') continue;
      if (!(c.deal_amount > 0) && !c.deal_stage) continue;
      out.push({
        key:            `legacy:${c.id}`,
        is_legacy:      true,
        deal_id:        c.id,                       // fallback key for actions
        deal_number:    `LGC-${shortId(c.id).toUpperCase()}`,
        client_id:      c.id,
        client_name:    c.full_name,
        client_email:   c.email,
        client_phone:   c.phone,
        deal_stage:     deriveStageFromClient(c),
        deal_amount:    Number(c.deal_amount || 0),
        collected:      Number(c.amount_collected || 0),
        source:         c.deal_source || '—',
        rep:            c.sales_rep_name || '—',
        last_activity:  c.updated_at || c.created_at,
        raw_deal:       null,
        raw_client:     c,
      });
    }
    // Newest first
    out.sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0));
    return out;
  }, [rawDeals, rawClients]);

  // ── Filter ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (stageFilter && r.deal_stage !== stageFilter) return false;
      if (!q) return true;
      return (
        (r.client_name || '').toLowerCase().includes(q) ||
        (r.client_email || '').toLowerCase().includes(q) ||
        (r.deal_number  || '').toLowerCase().includes(q) ||
        (r.deal_id      || '').toLowerCase().includes(q) ||
        (r.client_id    || '').toLowerCase().includes(q)
      );
    });
  }, [rows, query, stageFilter]);

  // ── Metrics strip ──────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const total = rows.length;
    const value = rows.reduce((s, r) => s + (r.deal_amount || 0), 0);
    const collected = rows.reduce((s, r) => s + (r.collected || 0), 0);
    const openStages = new Set(['closed', 'submitted_to_partner']);
    const openDeals = rows.filter(r => !openStages.has(r.deal_stage)).length;
    return { total, value, collected, openDeals };
  }, [rows]);

  // ── Deep-link ?deal=<id> auto-opens the full Deal Workspace ────────
  useEffect(() => {
    if (loading) return;
    const dealParam = searchParams.get('deal');
    if (!dealParam) return;
    // v3.21-1716 · New route architecture: one full page per Deal.
    // Real Deals (with a real ptc_deals row) navigate to
    // `/ptc-deals/:dealId`.  Legacy client-level snapshots still open
    // the right-side drawer as a compatibility fallback.
    const hit = rows.find(r => r.deal_id === dealParam || r.client_id === dealParam);
    if (hit && !hit.is_legacy) {
      navigate(`/ptc-deals/${hit.deal_id}`, { replace: true });
      return;
    }
    if (hit && openDealKey !== hit.key) setOpenDealKey(hit.key);
  }, [loading, rows, searchParams, openDealKey, navigate]);

  const selected = useMemo(
    () => filtered.find(r => r.key === openDealKey) || rows.find(r => r.key === openDealKey) || null,
    [filtered, rows, openDealKey],
  );

  const closeDrawer = () => {
    setOpenDealKey(null);
    if (searchParams.get('deal')) {
      const sp = new URLSearchParams(searchParams);
      sp.delete('deal'); sp.delete('action');
      setSearchParams(sp, { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="deal-operations-workspace">
      {/* ── HEADER + compact metrics ─────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="workspace-title">Deal Operations Workspace</h1>
          <p className="text-xs text-slate-500">
            {metrics.total} deals · {metrics.openDeals} open · {money(metrics.value)} pipeline · {money(metrics.collected)} collected
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search client, deal #, or ID"
                   className="pl-9 h-9 text-xs" data-testid="workspace-search" />
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="gap-1.5 h-9" data-testid="workspace-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Stage filter chips (compact) ─────────────────────── */}
      <div className="flex flex-wrap gap-1" data-testid="workspace-stage-chips">
        <button
          onClick={() => setStageFilter('')}
          className={`text-[10px] px-2 py-1 rounded-full border transition ${!stageFilter ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
        >All ({rows.length})</button>
        {STAGES.map(s => {
          const cnt = rows.filter(r => r.deal_stage === s.id).length;
          if (cnt === 0) return null;
          const active = stageFilter === s.id;
          return (
            <button key={s.id} onClick={() => setStageFilter(active ? '' : s.id)}
                    className={`text-[10px] px-2 py-1 rounded-full border transition inline-flex items-center gap-1 ${active ? `${s.color} text-white border-transparent` : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid={`workspace-stage-chip-${s.id}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : s.color}`} />
              {s.label} ({cnt})
            </button>
          );
        })}
      </div>

      {/* ── LIST ─────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[11px] font-semibold w-[110px]">Deal #</TableHead>
                <TableHead className="text-[11px] font-semibold">Client</TableHead>
                <TableHead className="text-[11px] font-semibold w-[130px]">Stage</TableHead>
                <TableHead className="text-[11px] font-semibold w-[160px]">Next Action</TableHead>
                <TableHead className="text-[11px] font-semibold text-right w-[100px]">Total</TableHead>
                <TableHead className="text-[11px] font-semibold text-right w-[100px]">Collected</TableHead>
                <TableHead className="text-[11px] font-semibold w-[100px]">Rep</TableHead>
                <TableHead className="text-[11px] font-semibold w-[110px]">Last Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-400 text-xs">No deals match the current filter.</TableCell>
                </TableRow>
              ) : filtered.map(r => {
                const s = STAGE_MAP[r.deal_stage] || STAGE_MAP.new_lead;
                const na = NEXT_ACTION[r.deal_stage] || NEXT_ACTION.new_lead;
                const active = openDealKey === r.key;
                return (
                  <TableRow key={r.key} onClick={() => { if (r.is_legacy) { setOpenDealKey(active ? null : r.key); } else { navigate(`/ptc-deals/${r.deal_id}`); } }}
                            className={`cursor-pointer ${active ? 'bg-accent/5 border-l-2 border-l-accent' : 'hover:bg-slate-50'}`}
                            data-testid={`deal-row-${r.deal_id}`}>
                    <TableCell className="py-2 font-mono text-[11px] text-slate-700">
                      {r.deal_number}
                      {r.is_legacy && (
                        <Badge variant="outline" className="ml-1 text-[8px] px-1 py-0 border-amber-300 text-amber-700 bg-amber-50">legacy</Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="text-xs font-medium text-slate-800 truncate">{r.client_name}</div>
                      <div className="text-[10px] text-slate-400 truncate">{r.client_email || r.client_phone || '—'}</div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge className={`${s.color} text-white text-[10px] px-1.5 py-0`}>{s.label}</Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1 text-xs text-slate-600">
                        <na.icon className="h-3 w-3 text-slate-400" />
                        {na.label}
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-right text-xs font-semibold">{money(r.deal_amount)}</TableCell>
                    <TableCell className="py-2 text-right text-xs font-semibold text-emerald-700">
                      {r.collected > 0 ? money(r.collected) : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-600 truncate">{r.rep}</TableCell>
                    <TableCell className="py-2 text-[11px] text-slate-500">{timeAgo(r.last_activity)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── DEAL DRAWER (right side) ─────────────────────────── */}
      <DealDrawer row={selected} open={!!selected} onClose={closeDrawer} navigate={navigate} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Deal drawer
// ═══════════════════════════════════════════════════════════════════
function DealDrawer({ row, open, onClose, navigate }) {
  const [ownerships, setOwnerships] = useState([]);
  const [ownLoading, setOwnLoading] = useState(false);
  const [proposals,  setProposals]  = useState([]);
  const [propLoading, setPropLoading] = useState(false);

  useEffect(() => {
    if (!row?.client_id) { setOwnerships([]); setProposals([]); return; }
    setOwnLoading(true);
    api.get(`/clients/${row.client_id}/ownerships`)
      .then(r => setOwnerships(r.data?.items || []))
      .catch(() => setOwnerships([]))
      .finally(() => setOwnLoading(false));
    setPropLoading(true);
    api.get(`/clients/${row.client_id}/proposals`)
      .then(r => setProposals(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setProposals([]))
      .finally(() => setPropLoading(false));
  }, [row?.client_id]);

  if (!row) return null;
  const s = STAGE_MAP[row.deal_stage] || STAGE_MAP.new_lead;
  const na = NEXT_ACTION[row.deal_stage] || NEXT_ACTION.new_lead;

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-[560px] w-full overflow-y-auto p-0" data-testid="deal-drawer">
        <SheetHeader className={`px-5 py-4 border-b ${s.color.replace('bg-', 'border-')} border-b-2`}>
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-base flex items-center gap-2" data-testid="drawer-title">
                <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded" data-testid="drawer-deal-number">{row.deal_number}</span>
                {row.client_name}
              </SheetTitle>
              <SheetDescription className="text-[11px] text-slate-500 mt-1 font-mono">
                Client: {row.client_id || '—'} · Deal: {row.deal_id}
              </SheetDescription>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="drawer-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={`${s.color} text-white text-[10px]`}>{s.label}</Badge>
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
              <Zap className="h-3 w-3 text-amber-500" />Next: {na.label}
            </span>
            <span className="text-[10px] text-slate-400">·</span>
            <span className="text-[10px] text-slate-600 font-semibold">{money(row.deal_amount)}</span>
            {row.is_legacy && (
              <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 bg-amber-50">legacy — no ptc_deals row</Badge>
            )}
          </div>
        </SheetHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="w-full h-9 bg-slate-50 rounded-none border-b justify-start px-3 overflow-x-auto">
            <TabsTrigger value="overview" className="text-[11px] h-7" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="ownership" className="text-[11px] h-7" data-testid="tab-ownership">Ownership</TabsTrigger>
            <TabsTrigger value="financing" className="text-[11px] h-7" data-testid="tab-financing">Financing / Offers</TabsTrigger>
            <TabsTrigger value="serviceauth" className="text-[11px] h-7" data-testid="tab-serviceauth">Service Auth &amp; Contract</TabsTrigger>
            <TabsTrigger value="closing"   className="text-[11px] h-7" data-testid="tab-closing">Closing Packet</TabsTrigger>
            <TabsTrigger value="activity"  className="text-[11px] h-7" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          {/* ── Overview ── */}
          <TabsContent value="overview" className="p-4 space-y-3">
            <FactRow icon={Building2}  label="Client"       value={row.client_name} sub={row.client_email || row.client_phone} />
            <FactRow icon={DollarSign} label="Deal Amount"  value={money(row.deal_amount)} sub={`Collected: ${row.collected > 0 ? money(row.collected) : '—'}`} />
            <FactRow icon={ClipboardList} label="Source"    value={row.source || '—'} sub={`Rep: ${row.rep}`} />
            <FactRow icon={ActivityIcon} label="Last Activity" value={timeAgo(row.last_activity)} sub={row.last_activity ? new Date(row.last_activity).toLocaleString() : ''} />
            <div className="pt-2 flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                      onClick={() => navigate(`/clients/${row.client_id}`)} data-testid="open-client-btn">
                <ExternalLink className="h-3.5 w-3.5" />Open Client Page
              </Button>
              <Button size="sm" className="h-8 text-xs gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white"
                      onClick={() => navigate(`/admin/closing-packet/${row.client_id}?deal=${row.deal_id}`)}
                      data-testid="open-closing-btn">
                <FileText className="h-3.5 w-3.5" />Closing Packet Builder
              </Button>
            </div>
          </TabsContent>

          {/* ── Ownership ── */}
          <TabsContent value="ownership" className="p-4 space-y-2" data-testid="ownership-tab-content">
            {ownLoading ? (
              <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div>
            ) : ownerships.length === 0 ? (
              <div className="text-xs text-slate-400 py-6 text-center">
                <Home className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                No ownerships recorded yet.
              </div>
            ) : ownerships.map(o => (
              <div key={o.id} className="border rounded p-2 text-xs bg-slate-50" data-testid={`ownership-${o.id}`}>
                <div className="font-semibold">{o.resort_name || '—'}</div>
                <div className="text-[10px] text-slate-500">
                  {o.resort_city || '—'}{o.resort_state ? `, ${o.resort_state}` : ''} · {o.ownership_type || '—'}
                  {o.points_owned ? ` · ${Number(o.points_owned).toLocaleString()} pts` : ''}
                </div>
                <div className="text-[10px] text-slate-500">
                  Maint: {money(o.annual_maintenance_fees)} · Balance: {money(o.loan_current_balance || 0)}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ── Financing / Offers ── */}
          <TabsContent value="financing" className="p-4 space-y-3">
            <div className="text-[11px] text-slate-500">
              Financing / Offer submissions require the following operator-verified fields:
            </div>
            <FinancingChecklist client={row.raw_client} />
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={() => navigate(`/clients/${row.client_id}#financing`)}>
              <ExternalLink className="h-3.5 w-3.5" />Open Financing on Client Page
            </Button>
          </TabsContent>

          {/* ── Service Authorization & Contract (v3.21-1715) ── */}
          <TabsContent value="serviceauth" className="p-4 space-y-3" data-testid="serviceauth-tab-content">
            <ServiceAuthPanel row={row} navigate={navigate} legacyProposals={proposals} legacyLoading={propLoading} />
          </TabsContent>

          {/* ── Closing Packet ── */}
          <TabsContent value="closing" className="p-4 space-y-3">
            <div className="text-xs text-slate-600">
              Closing Packet builder receives explicit
              <code className="mx-1 bg-slate-100 px-1 py-0.5 rounded text-[10px]">client_id</code>
              +
              <code className="mx-1 bg-slate-100 px-1 py-0.5 rounded text-[10px]">deal_id</code>
              from this drawer, so no email/name rediscovery occurs.
            </div>
            <Button size="sm" className="h-8 text-xs gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white"
                    onClick={() => navigate(`/admin/closing-packet/${row.client_id}?deal=${row.deal_id}`)}
                    data-testid="closing-tab-open-builder-btn">
              <FileText className="h-3.5 w-3.5" />Open Closing Packet Builder
            </Button>
          </TabsContent>

          {/* ── Activity ── */}
          <TabsContent value="activity" className="p-4">
            <div className="text-xs text-slate-600">Activity feed lives on the full Client Page.</div>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 mt-2" onClick={() => navigate(`/clients/${row.client_id}#activity`)}>
              <ExternalLink className="h-3.5 w-3.5" />Open Activity
            </Button>
          </TabsContent>

          {/* v3.21-1741: Compensation Visibility tab removed — the block never
              worked in practice and consumed drawer height for no operational
              value. The DealCompensationVisibilityCompact import is removed
              at the top of this file. All other tabs preserved. */}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// v3.21-1715 · Client Info & Service Authorization panel (drawer tab).
// Fully state-driven — Preview / Create / Send are strictly separate.
// Never issues a send without an existing revision + explicit confirm.
function ServiceAuthPanel({ row, navigate, legacyProposals, legacyLoading }) {
  const [ctx,         setCtx]         = useState(null);
  const [revs,        setRevs]        = useState([]);
  const [deliveries,  setDeliveries]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [creating,    setCreating]    = useState(false);
  const [sending,     setSending]     = useState(null); // revision_id being sent
  const [sendConfirm, setSendConfirm] = useState(null); // { rev, recipient }

  const isLegacy = row.is_legacy;
  const clientId = row.client_id;
  const dealId   = row.deal_id;

  const fetchAll = useCallback(async () => {
    if (isLegacy) return; // service-auth requires real Deal
    setLoading(true);
    try {
      const [ctxR, listR] = await Promise.all([
        api.get(`/clients/${clientId}/deals/${dealId}/service-auth/context`),
        api.get(`/clients/${clientId}/deals/${dealId}/service-auth/revisions`),
      ]);
      setCtx(ctxR.data);
      setRevs(listR.data?.revisions || []);
      setDeliveries(listR.data?.deliveries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Service Authorization state');
    } finally {
      setLoading(false);
    }
  }, [clientId, dealId, isLegacy]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/deals/${dealId}/service-auth/preview.pdf`;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await api.post(`/clients/${clientId}/deals/${dealId}/service-auth/create`, {});
      toast.success(`Service Authorization ${r.data.revision_number} created · SHA-${r.data.sha256.slice(0,8)}`);
      await fetchAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d
        : (d?.message ? d.message + (d.required_missing?.length ? ' — required: ' + d.required_missing.join(', ') : '')
                       : 'Create failed');
      toast.error(msg);
    } finally { setCreating(false); }
  };

  const handleSendConfirmed = async () => {
    if (!sendConfirm) return;
    setSending(sendConfirm.rev.id);
    try {
      const r = await api.post(`/service-auth/revision/${sendConfirm.rev.id}/send`, {
        recipient: sendConfirm.recipient, confirm: true,
      });
      if (r.data?.idempotent) {
        toast.info('Already sent to this recipient — delivery record reused (idempotent).');
      } else if (r.data?.status === 'DOCUMENT_SENT_SYNC_PENDING') {
        toast.warning('Sent, but downstream sync pending — Reconcile available.');
      } else {
        toast.success(`Sent to ${sendConfirm.recipient}`);
      }
      setSendConfirm(null);
      await fetchAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Send failed');
      toast.error(msg);
    } finally { setSending(null); }
  };

  const handleReconcile = async (deliveryId) => {
    try {
      await api.post(`/service-auth/delivery/${deliveryId}/reconcile`);
      toast.success('Reconciled — Activity + Communications + Deal stage synchronized.');
      await fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reconcile failed'); }
  };

  if (isLegacy) {
    return (
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded p-3">
        Service Authorization requires an authoritative Deal record. This is a
        legacy client-level row — create a real Deal via Ghost Deal Builder or
        the Client page to unlock Service Authorization actions.
      </div>
    );
  }

  const needsInput = ctx?.needs_input || [];
  const readyToCreate = !!ctx?.ready_to_create;
  const latestRev = revs[0];
  const lastDelivery = deliveries.find(d => d.revision_id === latestRev?.id);
  const status = !latestRev ? 'Not Created'
    : lastDelivery?.status === 'sent' ? 'Sent'
    : lastDelivery?.status === 'DOCUMENT_SENT_SYNC_PENDING' ? 'Document Sent — Sync Pending'
    : lastDelivery ? 'Delivery Pending'
    : (needsInput.length > 0 ? 'Created (Needs Input Present)' : 'Ready to Send');

  return (
    <div className="space-y-3" data-testid="serviceauth-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700">Client Info and Service Authorization</div>
        <Badge className={
          status.startsWith('Sent') ? 'bg-emerald-600 text-white text-[10px]'
          : status.includes('Sync Pending') ? 'bg-amber-500 text-white text-[10px]'
          : status.startsWith('Ready') || status.startsWith('Delivery') ? 'bg-blue-500 text-white text-[10px]'
          : status.startsWith('Created') ? 'bg-indigo-500 text-white text-[10px]'
          : 'bg-slate-500 text-white text-[10px]'
        } data-testid="serviceauth-status">{status}</Badge>
      </div>

      {loading && <div className="text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin inline mr-1"/>Loading…</div>}

      {ctx?.classification && (ctx.classification.required.length + ctx.classification.conditional_applicable.length) > 0 && (
        <div className="border border-rose-200 bg-rose-50 rounded p-2 text-[10px]" data-testid="serviceauth-needs-input">
          {ctx.classification.required.length > 0 && (
            <div className="mb-1.5">
              <div className="font-semibold text-rose-800 mb-1">
                REQUIRED — must fill ({ctx.classification.required.length}):
              </div>
              <div className="flex flex-wrap gap-1">
                {ctx.classification.required.map(k => (
                  <span key={k} className="bg-white border border-rose-300 rounded px-1.5 py-0.5 text-rose-700" data-testid={`needs-required-${k}`}>{k}</span>
                ))}
              </div>
            </div>
          )}
          {ctx.classification.conditional_applicable.length > 0 && (
            <div className="mb-1.5">
              <div className="font-semibold text-amber-800 mb-1">
                CONDITIONAL — required because trigger is set ({ctx.classification.conditional_applicable.length}):
              </div>
              <div className="flex flex-wrap gap-1">
                {ctx.classification.conditional_applicable.map(k => (
                  <span key={k} className="bg-white border border-amber-300 rounded px-1.5 py-0.5 text-amber-700">{k}</span>
                ))}
              </div>
            </div>
          )}
          {ctx.classification.optional.length > 0 && (
            <div className="mb-1.5">
              <div className="font-semibold text-slate-600 mb-1">
                Optional — will render blank if left empty ({ctx.classification.optional.length}):
              </div>
              <div className="flex flex-wrap gap-1">
                {ctx.classification.optional.map(k => (
                  <span key={k} className="bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-500">{k}</span>
                ))}
              </div>
            </div>
          )}
          {ctx.classification.conditional_not_applicable.length > 0 && (
            <div className="text-[10px] text-slate-400" data-testid="conditional-not-applicable">
              Not applicable ({ctx.classification.conditional_not_applicable.length} · e.g. Client 2 / Timeshare 2 fields with no trigger set).
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                onClick={() => window.open(previewUrl, '_blank')} data-testid="serviceauth-preview-btn">
          <ExternalLink className="h-3.5 w-3.5" />Preview Authorization
        </Button>
        <Button size="sm" className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleCreate} disabled={creating || !readyToCreate && !latestRev}
                data-testid="serviceauth-create-btn"
                title={!readyToCreate && !latestRev ? 'Some fields still show NEEDS INPUT' : ''}>
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          {latestRev ? 'Create New Revision' : 'Create Service Authorization'}
        </Button>
      </div>

      {revs.length > 0 && (
        <div className="space-y-2" data-testid="serviceauth-revisions">
          <div className="text-[10px] uppercase text-slate-400">Revisions</div>
          {revs.map(rev => {
            const del = deliveries.find(d => d.revision_id === rev.id);
            return (
              <div key={rev.id} className="border rounded p-2 bg-slate-50 text-xs">
                <div className="flex items-center justify-between">
                  <div className="font-mono font-semibold" data-testid={`serviceauth-rev-${rev.id}`}>
                    {rev.revision_number} · SHA-{rev.sha256.slice(0, 8)}
                  </div>
                  <div className="text-[10px] text-slate-500">{timeAgo(rev.created_at)}</div>
                </div>
                {del ? (
                  <div className="mt-1 text-[10px]">
                    <span className={del.status === 'sent' ? 'text-emerald-700 font-semibold'
                                                              : 'text-amber-700 font-semibold'}>
                      {del.status === 'sent' ? 'Sent' : del.status}
                    </span>
                    {' · to '}<span className="font-mono">{del.recipient}</span>
                    {' · '}{timeAgo(del.sent_at)}
                    {del.status === 'DOCUMENT_SENT_SYNC_PENDING' && (
                      <Button size="sm" variant="outline" className="ml-2 h-6 text-[10px] gap-1"
                              onClick={() => handleReconcile(del.id)} data-testid={`serviceauth-reconcile-${del.id}`}>
                        Reconcile
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/deals/${dealId}/service-auth/preview.pdf`, '_blank')}>
                      <ExternalLink className="h-3 w-3" />View
                    </Button>
                    <Button size="sm" className="h-6 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => setSendConfirm({ rev, recipient: row.client_email || '' })}
                            disabled={sending === rev.id}
                            data-testid={`serviceauth-send-btn-${rev.id}`}>
                      {sending === rev.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                      Send Service Authorization
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!legacyLoading && legacyProposals?.length > 0 && (
        <div className="border rounded p-2 bg-slate-50 text-[10px]" data-testid="serviceauth-legacy">
          <div className="font-semibold text-slate-600 mb-1">Legacy proposal artefacts (pre-v1715):</div>
          {legacyProposals.map(p => (
            <div key={p.id} className="text-slate-500">
              <span className="font-mono">{p.proposal_reference || p.id}</span> · {timeAgo(p.created_at)}
            </div>
          ))}
          <div className="text-[10px] text-slate-400 mt-1">
            These are historical Timeshare Exit Proposal emails and are NOT authoritative
            Client Info and Service Authorization documents. Use the operator repair panel
            on the Client page to reconcile linkage without resending.
          </div>
        </div>
      )}

      {/* Explicit send-confirmation modal */}
      {sendConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" role="dialog"
             data-testid="serviceauth-confirm">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 space-y-3">
            <div className="text-sm font-semibold">Confirm send</div>
            <div className="text-xs text-slate-600 space-y-1 font-mono">
              <div>Client: {row.client_name}</div>
              <div>Deal: {row.deal_number} · {row.deal_id.slice(0,8)}</div>
              <div>Revision: {sendConfirm.rev.revision_number}</div>
              <div>SHA-256: {sendConfirm.rev.sha256.slice(0, 16)}…</div>
              <div>Recipient:
                <input className="ml-1 border rounded px-1 py-0.5 font-mono text-[11px] w-56"
                       value={sendConfirm.recipient}
                       onChange={e => setSendConfirm({...sendConfirm, recipient: e.target.value})}
                       data-testid="serviceauth-confirm-recipient" />
              </div>
            </div>
            <div className="text-[10px] text-slate-500">
              The EXACT stored revision above will be sent — no regeneration during send.
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSendConfirm(null)}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                      onClick={handleSendConfirmed} disabled={!sendConfirm.recipient || sending}
                      data-testid="serviceauth-confirm-send-btn">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FactRow({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 text-xs">
      <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center shrink-0">
        <Icon className="h-3.5 w-3.5 text-slate-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase text-slate-400 tracking-wide">{label}</div>
        <div className="text-slate-800 font-medium truncate">{value}</div>
        {sub && <div className="text-[10px] text-slate-500 truncate">{sub}</div>}
      </div>
    </div>
  );
}

// v3.21-1715 · Explicit blocker list — replaces the old silent
// no-op "Send for Offers" button.
function FinancingChecklist({ client }) {
  const c = client || {};
  const rules = [
    { key: 'ssn',        label: 'SSN on file',        ok: !!c.ssn },
    { key: 'dob',        label: 'Date of Birth',      ok: !!c.dob },
    { key: 'income',     label: 'Annual Income',      ok: !!(c.annual_income || c.income) },
    { key: 'employment', label: 'Employer / Job',     ok: !!(c.employer || c.employment_status) },
    { key: 'residence',  label: 'Residence Address',  ok: !!(c.street_address || c.address) },
    { key: 'loan',       label: 'Requested Loan Amt', ok: !!c.requested_loan_amount },
    { key: 'phone',      label: 'Phone',              ok: !!c.phone },
    { key: 'email',      label: 'Email',              ok: !!c.email },
  ];
  const missing = rules.filter(r => !r.ok);
  return (
    <div className="border rounded p-2 bg-slate-50" data-testid="offer-blockers">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-slate-700">Offer Readiness</span>
        <Badge className={missing.length === 0 ? 'bg-emerald-600 text-white text-[10px]' : 'bg-amber-500 text-white text-[10px]'}>
          {missing.length === 0 ? 'Ready' : `Blocked · ${missing.length} missing`}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {rules.map(r => (
          <div key={r.key} className="flex items-center gap-1.5 text-[10px]">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.ok ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <span className={r.ok ? 'text-slate-600' : 'text-rose-700 font-medium'}>{r.label}</span>
          </div>
        ))}
      </div>
      {missing.length > 0 && (
        <div className="mt-2 text-[10px] text-rose-700">
          Send for Offers is disabled until the above items are captured on the Client Page.
          This replaces the previous silent no-op button.
        </div>
      )}
    </div>
  );
}
